import React, { ReactElement, ReactNode } from 'react';
import {
  alpha,
  Button,
  ButtonProps,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { globalTranslation, useTranslation } from 'modules/i18n';
import { IotaTokenIcon, StIotaTokenIcon } from 'modules/icons';

export interface ITextFieldProps extends Omit<MuiTextFieldProps, 'error'> {
  balance?: BigNumber;
  amountUsd: BigNumber;
  onMaxClick?: () => void;
  isStakeTab?: boolean;
  isBoostRewards?: boolean;
  error?: ReactNode;
  maxButtonProps?: ButtonProps;
}

export function TextField({
  onMaxClick,
  isStakeTab,
  isBoostRewards,
  amountUsd,
  disabled,
  error,
  maxButtonProps,
  ...props
}: ITextFieldProps): ReactElement {
  const { t, keys } = useTranslation(globalTranslation);
  const { classes } = useStyles({ isError: !!error });

  return (
    <div>
      <div className={classes.inputGroup}>
        <div className={classes.token}>
          {isStakeTab ? (
            <IotaTokenIcon className={classes.image} />
          ) : (
            <StIotaTokenIcon className={classes.image} />
          )}

          {isStakeTab ? t(keys.tokens.native) : t(keys.tokens.staked)}
        </div>

        <div className={classes.inputWrap}>
          <MuiTextField disabled={disabled} {...props} />

          {!amountUsd.isZero() && (
            <div className={classes.usdValue}>
              {t(keys.unit.$, { value: amountUsd.toFixed(2) })}
            </div>
          )}
        </div>

        {!isBoostRewards && (
          <Button
            disabled={disabled}
            size="small"
            variant="outlined"
            onClick={onMaxClick}
            {...maxButtonProps}
          >
            <div className={classes.text}>{t(keys.common.max)}</div>
          </Button>
        )}
      </div>

      {error && <div className={classes.error}>{error}</div>}
    </div>
  );
}

export const useStyles = makeStyles<{ isError: boolean }>()(
  (theme, { isError }) => ({
    inputGroup: {
      display: 'flex',
      alignItems: 'center',
      marginTop: theme.spacing(3),
      padding: theme.spacing(5, 4),
      border: `2px solid ${isError ? theme.palette.error.main : theme.palette.background.default}`,
      borderRadius: 12,
      backgroundColor: theme.palette.primary.contrastText,
    },

    token: {
      width: 120,
      display: 'flex',
      alignItems: 'center',
      height: 60,
      gap: theme.spacing(2),
      borderRight: `1px solid ${theme.palette.background.default}`,
      marginRight: theme.spacing(4),
      fontWeight: 600,
    },
    image: {
      width: 32,

      [theme.breakpoints.down('sm')]: {
        width: 24,
      },
    },

    inputWrap: {
      flex: 1,
      marginRight: theme.spacing(2),

      [theme.breakpoints.down('sm')]: {
        marginRight: theme.spacing(1),
      },
    },

    usdValue: {
      marginTop: theme.spacing(0.5),
      color: alpha(theme.palette.secondary.contrastText, 0.5),
      fontWeight: 400,
      fontSize: 12,
    },

    error: {
      position: 'absolute',
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.error.main,
      marginTop: theme.spacing(1.25),
    },

    text: {
      marginTop: theme.spacing(0.5),
    },
  }),
);
